"use strict";
// Core Firebase types shared between frontend and backend.
//
// PARITY: These shapes are normative-from-Swift (Stash/DomainModel in the iOS
// repo is the source of truth — see the iOS repo's
// plans/20260817-domain-model-parody.md). Persisted properties only. Wire
// facts that differ from "obvious" TypeScript modeling:
//   - Swift Codable omits nil keys — optionals here are `?` (key absent),
//     never `| null`.
//   - Swift associated-value enums encode as single-key objects
//     `{"<case>": {…payload…}}` (payload-less: `{"<case>": {}}`; unlabeled
//     payloads keyed `_0`). See the Case<> helper.
//   - Identifier naming quirks are real wire facts: `HomeBase.recordId`
//     (lowercase d), `StashCardActivity.id`, `StasherFriends` keyed by
//     `stasherID` with no separate recordID field.
// Every persisted model change must land with its parity fixture update
// (functions/tests/fixtures/domain-parity/) in the same PR.
Object.defineProperty(exports, "__esModule", { value: true });
exports.COUNTRY_CODE = exports.SMS_OPT_IN_STATUS = exports.STASHER_INVITE_COMMUNICATION_METHOD = exports.VERIFICATION_REQUEST_STATUS = exports.BUSINESS_ORG_RECORD_STATUS = exports.BUSINESS_VERIFICATION_STATUS = exports.BUSINESS_VERIFICATION_METHOD = exports.LLM_TYPE = exports.FLAG_STATUS = exports.COMMUNITY_PRIVACY = exports.CHAT_CHANNEL_TYPE = exports.PHONE_FORMAT = exports.ACTIVITY_TYPE = exports.RECORD_SOURCE = exports.FRIEND_STATUS = exports.DATA_DOCUMENT_VISIBILITY = exports.RECORD_STATUS = exports.SOCIAL_BOT_PLATFORM = exports.REFERENCE_INFO_ITEM_LABELS = void 0;
exports.caseName = caseName;
exports.buildMeta = buildMeta;
// The version of the app to use in Meta.appVersion when creating new records.
const SERVER_APP_VERSION = 'v2.10';
/**
 * The case name of a single-key associated-value enum object (wire rule:
 * `{"<case>": {…payload…}}`), e.g. `caseName({ call: { phone } })` → `'call'`.
 */
function caseName(value) {
    return Object.keys(value)[0] ?? '';
}
/**
 * Build a Meta instance.
 * @param existingMeta - Optional existing meta from the record being updated.
 *                       When provided, createdAt and appVersion are preserved.
 */
function buildMeta(existingMeta) {
    const now = new Date();
    return {
        createdAt: existingMeta?.createdAt ?? now,
        updatedAt: now,
        appVersion: existingMeta?.appVersion ?? SERVER_APP_VERSION,
    };
}
/** Advisory label vocabulary (Swift ReferenceInfoItemLabel) — not an enum on the wire. */
exports.REFERENCE_INFO_ITEM_LABELS = [
    'password', 'codeWord', 'pin', 'securityQuestion', 'accessCode',
    'twoFactorBackupCode', 'accountNumber', 'policyNumber', 'membershipNumber',
    'customerId', 'invoiceNumber', 'cardLast4', 'routingNumber', 'iban',
    'claimNumber', 'caseNumber', 'licenseNumber', 'contractId', 'patientId',
    'prescriptionNumber', 'insuranceGroupNumber', 'reservationNumber',
    'bookingNumber', 'serialNumber', 'deviceId', 'ticketNumber',
];
// ─────────────────────────────────────────────────────────────────────────────
// Enum constants (raw values verified against the Swift enums)
// ─────────────────────────────────────────────────────────────────────────────
exports.SOCIAL_BOT_PLATFORM = {
    GROUP_ME: 'groupMe'
};
/** Swift: DataDocumentStatus */
exports.RECORD_STATUS = {
    ACTIVE: 'active',
    DELETED: 'deleted'
};
/** Swift: DataDocumentVisibility */
exports.DATA_DOCUMENT_VISIBILITY = {
    OWNER_PRIVATE: 'ownerPrivate',
    COMMUNITY_PRIVATE: 'communityPrivate',
    GENERAL_PUBLIC: 'generalPublic'
};
/**
 * Swift: StasherFriends.FriendshipStatus encoded `base` values.
 * Decode-only legacy remaps: 'pending'→ACTIVE, 'accepted'→ACTIVE, 'rejected'→UNFRIENDED.
 */
exports.FRIEND_STATUS = {
    ACTIVE: 'active',
    UNFRIENDED: 'unfriended',
    BLOCKED: 'blocked',
    BLOCKING: 'blocking'
};
exports.RECORD_SOURCE = {
    IOS_APP: 'iOSApp',
    HERE_DIRECTORY: 'hereDirectory',
    ICLOUD_CONTACTS: 'iCloudContacts',
    GOOGLE_PLACES: 'googlePlaces',
    APPLE_MAP_KIT: 'appleMapKit',
    SPREADSHEET_IMPORT: 'Spreadsheet Import',
    PREVIEW_DATA: 'previewData',
    PHOTO_EXTRACTION: 'photoExtraction'
};
/**
 * @deprecated analytics labels only — the persisted StashCardActivity.activityType
 * is the single-key object union `StashCardActivityType`, not these strings.
 */
exports.ACTIVITY_TYPE = {
    STASHED: 'stashed',
    UNSTASHED: 'unstashed',
    EDITED: 'edited',
    CALL: 'call',
    CALL_COMPLETED: 'callCompleted',
    SMS: 'sms',
    EMAIL: 'email',
    MAP: 'map',
    SOCIAL: 'social',
    WEBSITE: 'website',
    ADD_TO_PLAYLIST: 'addToPlaylist',
    SHARE: 'share',
    OTHER: 'other'
};
/** App-layer formatting helper — not a persisted value. */
exports.PHONE_FORMAT = {
    E164: 'e164',
    NATIONAL: 'national',
    INTERNATIONAL: 'international'
};
exports.CHAT_CHANNEL_TYPE = {
    FRIEND_GROUP: 'friendGroup',
    CHAT: 'chat'
};
exports.COMMUNITY_PRIVACY = {
    OPEN: 'open',
    INVITE_ONLY: 'inviteOnly',
    ADMIN_APPROVAL_REQUIRED: 'adminApprovalRequired'
};
exports.FLAG_STATUS = {
    NONE: 'none',
    PENDING: 'pending',
    FLAG_REJECTED: 'flagRejected',
    FLAG_CONFIRMED: 'flagConfirmed'
};
/** Swift: LLMType */
exports.LLM_TYPE = {
    STASH: 'Stash',
    GPT_4_1_NANO: 'gpt-4.1-nano-2025-04-14'
};
exports.BUSINESS_VERIFICATION_METHOD = {
    EMAIL: 'email',
    SMS: 'sms',
    LEGACY_MIGRATION: 'legacy_migration'
};
exports.BUSINESS_VERIFICATION_STATUS = {
    PENDING: 'pending',
    VERIFIED: 'verified'
};
exports.BUSINESS_ORG_RECORD_STATUS = {
    ACTIVE: 'active',
    SUSPENDED: 'suspended'
};
exports.VERIFICATION_REQUEST_STATUS = {
    PENDING: 'pending',
    VERIFIED: 'verified',
    EXPIRED: 'expired'
};
exports.STASHER_INVITE_COMMUNICATION_METHOD = {
    PHONE: 'phone',
    EMAIL: 'email',
    NONE: 'none'
};
/** Swift: SMSOptInStatus (note the snake_case first value) */
exports.SMS_OPT_IN_STATUS = {
    NO_CONSENT: 'no_consent',
    SINGLE: 'single',
    DOUBLE: 'double'
};
/** Swift: CountryCode (ISO 3166-1 alpha-3 subset) */
exports.COUNTRY_CODE = {
    USA: 'USA', CAN: 'CAN', GBR: 'GBR', MEX: 'MEX', AUS: 'AUS', DEU: 'DEU',
    FRA: 'FRA', BRA: 'BRA', IND: 'IND', JPN: 'JPN', KOR: 'KOR', ESP: 'ESP'
};
